import React, { useState, useEffect, useRef } from "react";
import { styled, Theme, CSSObject } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { Box, List } from "@mui/material";
import { ReactComponent as NavUser } from "assets/Modal/NavUser.svg";
const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerContainer = styled("div")({
  overflow: "auto",
  flexGrow: 1,
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

interface NavigationItem {
  checked: boolean;
  href?: string;
  icon: React.ReactNode;
  title: string;
  tooltip: string;
  haveMenu?: boolean;
  menuItems?: { label: string; onClick: () => void }[];
  subItems?: Record<string, NavigationItem>;
}

interface DrawerComponentProps {
  open: boolean;
  navigation: Record<string, NavigationItem>;
  drawerHeader: React.ReactNode;
  themeColors?: any;
  isScreenLargeOrGreater: boolean;
  /** Extra top offset when login-as banner is visible */
  stackTopOffset?: number;
}

const DrawerComponent: React.FC<DrawerComponentProps> = ({
  open,
  navigation,
  drawerHeader,
  themeColors,
  isScreenLargeOrGreater,
  stackTopOffset = 0,
}) => {
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [, setSelectedSubItemId] = useState<string | null>(null);
  const navigate = useNavigate();
  const drawerContainerRef = useRef<HTMLDivElement>(null);
  const [scrollPosition] = useState<number>(0);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const { t } = useTranslation();
  const location = useLocation();
  const [submenuPosition, setSubmenuPosition] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });
  const [selectedSubItem, setSelectedSubItem] = useState<string | null>(
    localStorage.getItem("selectedSubItem") || null,
  );

  useEffect(() => {
    const currentPath = location.pathname;
    const matchingItemId = Object.keys(navigation).find((key) => {
      const item = navigation[key as keyof typeof navigation];
      if (item.subItems) {
        const subItemKeys = Object.keys(item.subItems);
        const subItemSelected = subItemKeys.some(
          (subKey) => item.subItems![subKey].href === currentPath,
        );
        if (subItemSelected) {
          return true;
        }
      }
      return item.href === currentPath;
    });
    if (currentPath.startsWith("/account-settings")) {
      setSelectedItemId("account-settings");
      return;
    }
    setSelectedItemId(matchingItemId || null);
  }, [location, navigation]);

  useEffect(() => {
    if (selectedItemId && drawerContainerRef.current) {
      const container = drawerContainerRef.current;
      const selectedElement = container.querySelector(
        `[data-key="${selectedItemId}"]`,
      );
      if (selectedElement) {
        const containerRect = container.getBoundingClientRect();
        const selectedRect = selectedElement.getBoundingClientRect();
        const scrollTop = selectedRect.top - containerRect.top + scrollPosition;
        container.scrollTo({ top: scrollTop, behavior: "smooth" });
      }
    }
  }, [selectedItemId, scrollPosition]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        drawerContainerRef.current &&
        !drawerContainerRef.current.contains(event.target as Node)
      ) {
        setOpenSubmenu(null);
      }
    };

    document.body.addEventListener("click", handleOutsideClick);

    return () => {
      document.body.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  const handleSubmenuToggle = (
    key: string,
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    setOpenSubmenu(openSubmenu === key ? null : key);
    setSelectedSubItemId(null);
    const selectedElement = event.currentTarget;
    const rect = selectedElement.getBoundingClientRect();
    // position:fixed requires viewport coordinates (not relative to drawer).
    // Keep submenu below the AppBar (+ login-as banner when present).
    const minTop = stackTopOffset + 72;
    setSubmenuPosition({
      top: Math.max(rect.top, minTop),
      left: rect.right,
    });
  };

  const handleSubItemClick = (
    parentKey: string,
    key: string,
    href?: string,
  ) => {
    setSelectedSubItem(key);
    setOpenSubmenu(null);
    if (href) {
      navigate(href);
    }
    localStorage.setItem("selectedSubItem", key);
  };

  const handleMouseEnter = (
    key: string,
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    if (!openSubmenu || openSubmenu !== key) {
      handleSubmenuToggle(key, event);
    }
  };

  const handleMouseLeave = () => {
    setOpenSubmenu(null);
  };

  const trimText = (text: string, maxLength: number) => {
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  return (
    <Drawer
      variant="permanent"
      open={open}
      className="hide-on-print"
      sx={{
        position: isScreenLargeOrGreater
          ? "unset"
          : open
            ? "absolute"
            : "unset",
        ...(stackTopOffset
          ? {
              "& .MuiDrawer-paper": {
                top: stackTopOffset,
                height: `calc(100% - ${stackTopOffset}px)`,
              },
            }
          : {}),
      }}
    >
      {drawerHeader && drawerHeader}
      <Divider />
      <DrawerContainer
        className={`bg-layoutbg-color border-t-4 ${
          openSubmenu ? "overflow-hidden" : "overflow-auto"
        }`}
        ref={drawerContainerRef}
      >
        {Object.keys(navigation).map((key) => {
          const item = navigation[key as keyof typeof navigation];
          const hasSubmenu =
            item.subItems && Object.keys(item.subItems).length > 0;
          const overflowClass = hasSubmenu ? "overflow-hidden" : "";

          // Ensure the main item is only rendered if `item.checked` is true
          if (!item.checked) {
            return null;
          }

          return (
            <div
              key={item.title}
              onMouseEnter={(event) => handleMouseEnter(key, event)}
              onMouseLeave={handleMouseLeave}
            >
              {Object.values(item.subItems || {}).every(
                (subItem) => subItem.checked === false,
              ) ? null : (
                <ListItem
                  disablePadding
                  className={`block ${overflowClass}`}
                  data-key={key}
                >
                  <Tooltip
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: "offset",
                            options: {
                              offset: [-35, -14],
                            },
                          },
                        ],
                      },
                    }}
                    title={t(item.tooltip)}
                    placement="right"
                  >
                    <ListItemButton
                      className={`group hover:text-white`}
                      aria-owns={open ? "mouse-over-popover" : undefined}
                      aria-haspopup="true"
                      sx={{
                        minHeight: 44,
                        justifyContent: open ? "initial" : "center",
                        px: 2.5,
                        background:
                          selectedItemId === key || openSubmenu === key
                            ? `${themeColors?.primary}`
                            : "",
                        color:
                          selectedItemId === key || openSubmenu === key
                            ? `#fff`
                            : "",
                        "&:hover": {
                          backgroundColor: themeColors?.primary,
                        },
                      }}
                    >
                      <ListItemIcon
                        className={` ${
                          selectedItemId === key || openSubmenu === key
                            ? "invert"
                            : ""
                        } `}
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : "auto",
                          justifyContent: "center",
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primaryTypographyProps={{
                          className: "font-Saira font-[500] text-[12px]",
                        }}
                        className={`opacity-${open ? 1 : 0}`}
                        primary={t(item.title)}
                      />
                      {hasSubmenu && open && (
                        <ArrowRightIcon
                          style={{
                            color:
                              selectedItemId === key
                                ? themeColors?.secondary
                                : "",
                          }}
                        />
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              )}
              {hasSubmenu && openSubmenu === key && (
                <div
                  style={{
                    position: "fixed",
                    top: submenuPosition.top,
                    left: submenuPosition.left,
                    zIndex: 1300,
                  }}
                >
                  <List
                    className="w-[13rem] pt-2 pb-1 rounded-tr-lg rounded-b-lg max-h-80 overflow-y-scroll"
                    style={{ background: themeColors?.primary }}
                  >
                    {Object.values(item.subItems || {}).map(
                      (subItem) =>
                        subItem.checked && (
                          <ListItem
                            key={subItem.title}
                            disablePadding
                            onClick={() =>
                              handleSubItemClick(
                                key,
                                subItem.title,
                                subItem.href,
                              )
                            }
                            className={`block overflow-hidden`}
                          >
                            <Tooltip
                              title={t(subItem.tooltip)}
                              placement="right"
                            >
                              <ListItemButton
                                className={`text-white mb-1 ml-1 py-1 pr-10`}
                                onClick={() =>
                                  handleSubItemClick(
                                    key,
                                    subItem.title,
                                    subItem.href,
                                  )
                                }
                                aria-haspopup="true"
                                sx={{
                                  px: 2.5,
                                  background:
                                    selectedSubItem === subItem.title
                                      ? `${themeColors?.secondary}`
                                      : "",
                                  "&:hover": {
                                    background: themeColors?.secondary,
                                  },
                                }}
                              >
                                <ListItemText
                                  primaryTypographyProps={{
                                    className:
                                      "font-Saira font-[500] text-[13px]",
                                  }}
                                  primary={trimText(t(subItem.title), 150)}
                                />
                              </ListItemButton>
                            </Tooltip>
                          </ListItem>
                        ),
                    )}
                  </List>
                </div>
              )}
            </div>
          );
        })}
      </DrawerContainer>
      <Divider />
      <Box
        sx={{
          mt: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Account Settings */}
        <ListItem disablePadding data-key="account-settings">
          <Tooltip
            slotProps={{
              popper: {
                modifiers: [
                  { name: "offset", options: { offset: [-35, -14] } },
                ],
              },
            }}
            title={t("Account Settings")}
            placement="right"
          >
            <ListItemButton
              onClick={() => {
                navigate("/account-settings/change-password");
                setSelectedItemId("account-settings");
              }}
              sx={{
                minHeight: 44,
                justifyContent: open ? "initial" : "center",
                px: 2.5,
                background:
                  selectedItemId === "account-settings"
                    ? themeColors?.primary
                    : "",
                color: selectedItemId === "account-settings" ? "#fff" : "",
                "&:hover": {
                  backgroundColor: themeColors?.primary,
                  color: "#fff",
                  "svg path": { fill: "#fff !important" },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : "auto",
                  justifyContent: "center",
                  marginRight: open ? "20px" : "0",
                  ...(selectedItemId === "account-settings" && {
                    "svg path": { fill: "#fff !important" },
                  }),
                }}
              >
                <NavUser />
              </ListItemIcon>
              <ListItemText
                primaryTypographyProps={{
                  className: "font-Saira font-[500] text-[13px]",
                }}
                className={`opacity-${open ? 1 : 0}`}
                primary={t("Account Settings")}
              />
            </ListItemButton>
          </Tooltip>
        </ListItem>
      </Box>
    </Drawer>
  );
};

export default DrawerComponent;
